import {
  ActionFields,
  createLocaleGroupGQLMutation,
  createLocaleSpaceGQLMutation,
  deleteLocaleGroupGQLMutation,
  getLocaleGroupGQLQuery,
  getLocaleGroupsGQLQuery,
  getSpaceByLocaleGQLQuery,
  LocaleGroupFields,
  removeSpaceFromLocaleGroupGQLMutation,
  SpaceFields,
  updateLocaleGroupGQLMutation,
} from '../../graphql/index.js'
import {
  Action,
  LocaleGroup,
  MutationCreateLocaleGroupArgs,
  MutationCreateLocaleSpaceArgs,
  MutationDeleteLocaleGroupArgs,
  MutationRemoveSpaceFromLocaleGroupArgs,
  MutationUpdateLocaleGroupArgs,
  QueryLocaleGroupArgs,
  QuerySpaceByLocaleArgs,
  Space,
} from '../../types/index.js'
import { GraphqlClient } from '../graphql.client.js'

export class LocaleGroupClient {
  private client: GraphqlClient

  constructor(client: GraphqlClient) {
    this.client = client
  }

  async get(
    variables: QueryLocaleGroupArgs,
    fields: LocaleGroupFields = 'default',
    accessToken?: string,
  ): Promise<LocaleGroup> {
    type QueryResult = { localeGroup: LocaleGroup }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: getLocaleGroupGQLQuery(fields),
      variables,
      customToken: accessToken,
    })
    return result.localeGroup
  }

  async list(
    fields: LocaleGroupFields = 'default',
    accessToken?: string,
  ): Promise<LocaleGroup[]> {
    type QueryResult = { localeGroups: LocaleGroup[] }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: getLocaleGroupsGQLQuery(fields),
      variables: {},
      customToken: accessToken,
    })
    return result.localeGroups
  }

  async spaceByLocale(
    variables: QuerySpaceByLocaleArgs,
    fields: SpaceFields = 'basic',
    accessToken?: string,
  ): Promise<Space> {
    type QueryResult = { spaceByLocale: Space }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: getSpaceByLocaleGQLQuery(fields),
      variables,
      customToken: accessToken,
    })
    return result.spaceByLocale
  }

  async create(
    variables: MutationCreateLocaleGroupArgs,
    fields: LocaleGroupFields = 'default',
    accessToken?: string,
  ): Promise<LocaleGroup> {
    type QueryResult = { createLocaleGroup: LocaleGroup }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: createLocaleGroupGQLMutation(fields),
      variables,
      customToken: accessToken,
    })
    return result.createLocaleGroup
  }

  async update(
    variables: MutationUpdateLocaleGroupArgs,
    fields: ActionFields = 'basic',
    accessToken?: string,
  ): Promise<Action> {
    type QueryResult = { updateLocaleGroup: Action }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: updateLocaleGroupGQLMutation(fields),
      variables,
      customToken: accessToken,
    })
    return result.updateLocaleGroup
  }

  async delete(
    variables: MutationDeleteLocaleGroupArgs,
    fields: ActionFields = 'basic',
    accessToken?: string,
  ): Promise<Action> {
    type QueryResult = { deleteLocaleGroup: Action }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: deleteLocaleGroupGQLMutation(fields),
      variables,
      customToken: accessToken,
    })
    return result.deleteLocaleGroup
  }

  async createLocaleSpace(
    variables: MutationCreateLocaleSpaceArgs,
    fields: SpaceFields = 'basic',
    accessToken?: string,
  ): Promise<Space> {
    type QueryResult = { createLocaleSpace: Space }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: createLocaleSpaceGQLMutation(fields),
      variables,
      customToken: accessToken,
    })
    return result.createLocaleSpace
  }

  async removeSpace(
    variables: MutationRemoveSpaceFromLocaleGroupArgs,
    fields: ActionFields = 'basic',
    accessToken?: string,
  ): Promise<Action> {
    type QueryResult = { removeSpaceFromLocaleGroup: Action }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: removeSpaceFromLocaleGroupGQLMutation(fields),
      variables,
      customToken: accessToken,
    })
    return result.removeSpaceFromLocaleGroup
  }
}
